/**
 * Outage detection — minimal version. Unplanned outages only.
 *
 * Scope: show the maintenance page when the API is genuinely down, hide it when
 * it comes back. No planned-maintenance switch, no new AWS infrastructure, no
 * Datadog changes. Three edits per app.
 *
 * THE ONE IDEA THAT MAKES THIS SAFE: it self-heals. The page clears itself
 * after config.maxShowMs no matter what, then re-evaluates. So the worst case for a
 * false positive is a wrong page for two minutes — not a wrong page until
 * someone ships a release. Five of these apps have no remote kill switch, which
 * is why that property is worth more here than any amount of cleverness in the
 * detection itself.
 *
 * WHY A SAME-ORIGIN PROBE AND NOT navigator.onLine: a dead API and a dead wifi
 * connection produce the identical opaque failure in the browser, because CORS
 * headers come from application middleware and an nginx/ALB 5xx never reaches
 * it. navigator.onLine can't break the tie — MDN documents it as unreliable,
 * and there are open Chrome/Firefox bugs where it sticks `false` while the
 * machine is online. Our own CDN is different infrastructure from the API, so
 * "our CDN answers but the API doesn't" is the actual signal.
 */

type Listener = () => void;

const listeners = new Set<Listener>();
let down = false;
let healTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * How many DISTINCT endpoints must be failing before we act.
 *
 * Counting raw failures is wrong twice over. React Query is configured
 * `retry: 1` in these apps, so a single broken endpoint produces two failures
 * on its own — raw counting would black out the whole app for one bad view.
 * And several endpoints failing can still mean one broken subsystem (the
 * AliExpress integration, say) rather than a dead API; that must not hide
 * someone's orders page.
 */
export const config = {
  /** Distinct endpoints that must be failing before this counts as an outage. */
  pathsRequired: 3,
  /** How long a failure, or a success, stays relevant. */
  failureWindowMs: 20_000,
  /** Hard ceiling on how long the page may show without being re-proven. */
  maxShowMs: 120_000,
  /** Longest gap between recovery attempts. */
  maxBackoffMs: 30_000,
  /** Delay before the first recovery attempt. */
  initialRecoveryMs: 2_000,
  /**
   * A cheap asset on the app's OWN origin. Used to tell "the API is down" from
   * "this user's connection is down" — the two are indistinguishable from the
   * failed request alone. Stylesi: `/favicon.svg` (2.4KB, confirmed present at
   * the CDN root under `public/`). `/logo.svg` also exists here (the full
   * wordmark, used by the page itself) but favicon.svg is the cheaper probe.
   */
  selfProbe: "/favicon.svg",
};

/** path -> time last seen failing, inside the window */
const failing = new Map<string, number>();
/** Time of the last SUCCESSFUL response. This is the decisive signal: if
 *  anything at all is working, the API is not down, whatever else is failing. */
let lastSuccess = 0;
let checking = false;

/** Collapse ids so `/orders/1/` and `/orders/2/` are one endpoint, not two —
 *  otherwise a single list view retrying across rows looks like a broad
 *  outage. */
function endpointOf(url?: string): string {
  if (!url) return "unknown";
  const path = url.replace(/^https?:\/\/[^/]+/, "").split("?")[0];
  return path
    .replace(/\/\d+(?=\/|$)/g, "/:id")
    .replace(/\/[0-9a-f]{8}-[0-9a-f-]{27}(?=\/|$)/gi, "/:uuid");
}

function emit() {
  listeners.forEach((l) => l());
}

export const maintenance = {
  subscribe(l: Listener) {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  getSnapshot: () => down,
};

function show() {
  if (down) return;
  down = true;
  failing.clear();
  emit();
  // Self-heal. Unconditional: if recovery polling has died, thrown, or was
  // never started, the page still comes back on its own.
  healTimer = setTimeout(hide, config.maxShowMs);
}

function hide() {
  if (!down) return;
  down = false;
  clearTimeout(healTimer);
  emit();
}

async function ok(url: string): Promise<boolean> {
  try {
    const res = await fetch(url, {
      cache: "no-store",
      signal: AbortSignal.timeout(5000),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Call from the axios response interceptor's error path. Never throws.
 *
 * `status` is `error.response?.status` — undefined when the response was
 * opaque, which is what an infrastructure-level outage looks like.
 */
export function noteFailure(status: number | undefined, url?: string): void {
  if (down || checking) return;
  if (status !== undefined && status < 500) return; // 401/403/404 are normal

  const now = Date.now();
  for (const [p, t] of failing) {
    if (now - t > config.failureWindowMs) failing.delete(p);
  }
  failing.set(endpointOf(url), now);

  // Not enough of the surface is broken to call this an outage.
  if (failing.size < config.pathsRequired) return;

  // Something answered successfully inside the window, so the API is alive and
  // this is a partial failure — a broken subsystem, not downtime. Never take
  // the whole app away while part of it still works.
  if (now - lastSuccess < config.failureWindowMs) return;

  checking = true;
  void (async () => {
    try {
      // Our CDN is different infrastructure from the API. If it answers, this
      // user's connection is fine, so the API failing is real. If it doesn't,
      // their network is the problem and a maintenance page would be a lie.
      if (await ok(`${config.selfProbe}?cb=${Date.now()}`)) show();
    } finally {
      checking = false;
    }
  })();
}

/** Call on every successful response. This is load-bearing, not bookkeeping:
 *  a live success is the strongest available evidence that the API is up. */
export function noteSuccess(): void {
  lastSuccess = Date.now();
  if (failing.size) failing.clear();
}

/**
 * Recovery. Mounted by the gate only while the page is showing, so it costs
 * nothing when healthy.
 *
 * `probe` should hit a real endpoint with raw fetch — NOT the app's own request
 * wrapper, whose 401 handling calls forceSignOut() and would log out every
 * parked tab. Any response at all proves the app server is running, because a
 * CORS-valid reply can only come from the application itself.
 */
export function startRecovery(probe: () => Promise<boolean>): () => void {
  let stopped = false;
  let attempt = 0;
  let timer: ReturnType<typeof setTimeout>;

  const tick = async () => {
    if (stopped) return;
    let recovered = false;
    try {
      recovered = await probe();
    } catch {
      recovered = false; // a throwing probe must not kill the loop
    }
    if (stopped) return;
    if (recovered) {
      hide();
      return;
    }
    attempt += 1;
    const ceiling = Math.min(
      config.maxBackoffMs,
      2_000 * 2 ** Math.min(attempt, 4)
    );
    timer = setTimeout(tick, Math.random() * ceiling); // full jitter
  };

  timer = setTimeout(tick, config.initialRecoveryMs * (1 + Math.random()));
  return () => {
    stopped = true;
    clearTimeout(timer);
  };
}
